'use client';

import React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';

import { DARK_COLORS } from '@/constants';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';
import useForceUpdate from '@/hooks/use-force-update';

const CONFIG = {
  tension: 150,
  friction: 8,
  precision: 0.00001,
};

const J_DELAY = 240;
const O_DELAY = 200;
const Y_DELAY = 300;

interface Props extends React.HTMLAttributes<HTMLAnchorElement> {
  baseDelay?: number;
}

function JoyLogo({ baseDelay = 0, ...delegated }: Props) {
  const prefersReducedMotion = usePrefersReducedMotion();

  // In production, the animation works correctly, but in development, the `from` state is fixed until the component re-renders for some other reason. Not sure why, it seems like a bug specific to React Spring + App Router.
  // To work around this, we’ll trigger a force update after mount, but only in development.
  const forceUpdate = useForceUpdate();
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      forceUpdate();
    }
  }, [forceUpdate]);

  const jSpring = useSpring({
    from: {
      transform: 'scale(0.75) rotate(-27deg)',
    },
    transform: 'scale(1) rotate(0deg)',
    delay: baseDelay + J_DELAY,
    config: { ...CONFIG, friction: 10 },
    immediate: prefersReducedMotion,
  });
  const oSpring = useSpring({
    from: {
      transform: 'scale(0.7) rotate(-20deg) translateY(40px)',
    },
    transform: 'scale(1) rotate(0deg) translateY(0px)',
    delay: baseDelay + O_DELAY,
    config: CONFIG,
    immediate: prefersReducedMotion,
  });
  const ySpring = useSpring({
    from: {
      transform: 'scale(0.8) rotate(30deg) translate(0px, 0px)',
    },
    transform: 'scale(1) rotate(8deg) translate(-10px, -16px)',
    delay: baseDelay + Y_DELAY,
    config: { ...CONFIG, friction: 12 },
    immediate: prefersReducedMotion,
  });

  return (
    <Wrapper {...delegated} href="https://joyforjs.com">
      <Svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 582 245"
        aria-hidden="true"
      >
        <Letter
          style={{
            ...jSpring,
            // In addition to this spring transform, there is also a keyframe animation that fades the letters in. We won’t change this value with React Spring, but I'll include it here:
            animationDelay: `${J_DELAY}ms`,
          }}
        >
          <path
            fill="var(--color-primary)"
            d="M95.014 131.673c-11-9-24-10.5-24-10.5V43.118c0-12.335 5.548-24.23 17.127-28.482 7.993-2.935 18.784-5.551 32.373-5.963 14.606-.443 30.686 4.992 38.5 11.188 9.618 7.626 9.51 16.405 9.5 16.798v125.019c-.046.681-4.385 63.036-74.5 70.495-57 6.063-79.301-25.026-84-33-4.594-7.798-2.595-25.681-2.503-26.473v-.009c.003-.016.003-.017.004-.04.04-.803 1.387-24.238 23.497-38.978 9-6 19.57-6.975 27.002-7 7.431-.026 14 0 26 7.5 10.664 6.665 17.774 20.043 19.225 22.939.174.346.524.561.905.489.936-.178 2.659-.794 3.37-2.928.885-2.656.203-4.92.035-5.405a1.502 1.502 0 01-.055-.196c-.209-.98-2.178-8.97-12.48-17.399z"
          ></path>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M64.0139 119.796C61.886 119.659 59.913 119.666 58.0752 119.672L57.9901 119.673C50.1487 119.699 37.8717 120.686 27.1285 127.848C2.98546 143.944 0.723583 169.145 0.536331 172.08C0.41897 173.149 -0.0571463 177.852 0.00568607 183.389C0.0395348 186.371 0.230555 189.741 0.764153 192.992C1.28222 196.148 2.20107 199.702 3.9829 202.726C6.74322 207.411 14.2391 218.161 28.7867 226.932C43.4378 235.765 64.8027 242.32 94.7544 239.133C132.226 235.147 152.695 216.264 163.56 197.956C174.22 179.992 175.443 162.974 175.498 162.143M64.0139 119.796V43.1177C64.0139 29.0364 70.4241 13.6848 85.7276 8.065C94.381 4.88734 105.914 2.11196 120.302 1.67595C128.493 1.42773 136.931 2.82119 144.351 5.08092C151.7 7.31922 158.553 10.5614 163.363 14.3757C175.141 23.7152 175.542 34.8317 175.514 36.743V161.59C175.514 161.627 175.514 161.668 175.514 161.712C175.514 161.786 175.512 161.854 175.51 161.914C175.506 162.022 175.5 162.109 175.498 162.143M71.0139 121.173C71.0139 121.173 72.4733 121.341 74.828 121.904C79.501 123.02 87.6996 125.688 95.0139 131.673C105.316 140.102 107.285 148.092 107.494 149.072C107.508 149.139 107.526 149.203 107.549 149.268C107.628 149.498 107.824 150.127 107.913 151.008C107.945 151.326 107.963 151.676 107.957 152.052C107.945 152.835 107.828 153.729 107.514 154.673C107.471 154.8 107.425 154.922 107.376 155.039C106.6 156.879 105.024 157.433 104.144 157.601C103.763 157.673 103.413 157.458 103.239 157.112C101.788 154.216 94.6779 140.838 84.0139 134.173C79.7023 131.478 76.0919 129.748 72.8907 128.639C71.9779 128.323 71.0984 128.057 70.2453 127.834C70.2447 127.833 70.2441 127.833 70.2434 127.833C68.0276 127.253 65.9907 126.96 64.0139 126.813C61.9932 126.663 60.0355 126.666 58.0139 126.673C50.5824 126.698 40.0113 127.673 31.0114 133.673C8.90156 148.413 7.55474 171.848 7.51489 172.651C7.51379 172.674 7.51366 172.675 7.51173 172.691L7.51072 172.7C7.41944 173.492 5.41965 191.375 10.0139 199.173C14.7127 207.147 37.0139 238.236 94.0139 232.173C164.129 224.714 168.468 162.359 168.514 161.678C168.514 161.665 168.514 161.662 168.514 161.656C168.514 161.653 168.514 161.649 168.514 161.641V36.6916C168.514 36.6831 168.514 36.677 168.514 36.6701L168.514 36.6591C168.524 36.2665 168.632 27.4869 159.014 19.8606C151.2 13.6648 135.12 8.23013 120.514 8.67274C106.925 9.08453 96.1338 11.7007 88.1406 14.636C76.5622 18.8878 71.0139 30.7833 71.0139 43.1177V121.173ZM100.647 150.528C100.722 150.879 100.813 151.203 100.914 151.5ZM0.559001 171.879L0.55866 171.882ZM7.51308 172.651L6.91812 172.622ZM6.91158 172.621L6.91812 172.622Z"
            fill="var(--color-background)"
          />
          <g filter="url(#filter0_f_124_43)">
            <path
              stroke="url(#paint0_linear_124_43)"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeOpacity="0.63"
              strokeWidth="16"
              d="M32.557 152.435c-8.944 8.898-8.222 23.212-8.222 23.212L35.5 155.5l18.446-10.803s-12.444-1.16-21.389 7.738z"
            ></path>
          </g>
          <mask
            id="mask0_124_43"
            style={{ maskType: 'alpha' }}
            width="70"
            height="130"
            x="54"
            y="16"
            maskUnits="userSpaceOnUse"
          >
            <path
              fill="#D9D9D9"
              fillRule="evenodd"
              d="M124 16H54v105.134C59.459 117.866 65.773 116 72.5 116c17.26 0 31.795 12.282 36.161 29H124V16z"
              clipRule="evenodd"
            ></path>
          </mask>
          <g
            filter="url(#filter1_f_124_43)"
            mask="url(#mask0_124_43)"
          >
            <path
              stroke="#fff"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeOpacity="0.63"
              strokeWidth="16"
              d="M85.445 112.5V48.148s0-14.5 10-20"
            ></path>
          </g>
        </Letter>

        <Letter
          style={{ ...oSpring, animationDelay: `${O_DELAY}ms` }}
        >
          <path
            fill="var(--color-primary)"
            stroke="var(--color-background)"
            strokeWidth="7"
            d="M276.445 241.147c31.542 0 59.108-10.128 78.802-29.572 19.707-19.457 31.198-47.914 31.198-83.928 0-35.893-10.188-66.07-29.737-87.308-19.585-21.277-48.21-33.192-84.263-33.192-36.134 0-64.249 12.845-83.297 34.169-19 21.27-28.703 50.66-28.703 83.331 0 38.827 13.036 68.069 34.291 87.579 21.206 19.464 50.241 28.921 81.709 28.921z"
          ></path>
          <path
            fill="var(--color-background)"
            d="M270.945 107.647c.626 14.389-1.075 44.351-1.434 50.416-.041.686.278 1.326.906 1.607.745.335 1.919.747 3.528.977 7 1 7-17.5 7-17.5l.496-23.308c.003-.128.004-.251.004-.379-.002-1.705-.033-14.58-.5-18.313-.5-4 0-12-6-14-2.481-.827-4.236.567-5.028 1.418-.329.354-.434.845-.373 1.325.264 2.083.996 8.445 1.401 17.757z"
          ></path>
          <g filter="url(#filter2_f_124_43)">
            <path
              stroke="url(#paint1_linear_124_43)"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeOpacity="var(--highlight-opacity)"
              strokeWidth="16"
              d="M185.946 108.147s2.5-34.89 27.5-56.5c24.999-21.61 41-22 41-22"
            ></path>
          </g>
        </Letter>

        <Letter
          style={{ ...ySpring, animationDelay: `${Y_DELAY}ms` }}
        >
          <path
            fill="var(--color-primary)"
            stroke="var(--color-background)"
            strokeWidth="7"
            d="M470.12 68.756l.241-.777-.126-.804-3.458.544 3.458-.544h0l-.001-.003-.001-.008-.005-.03-.018-.112-.069-.43a382.285 382.285 0 00-1.252-7.198c-.837-4.55-2.014-10.488-3.366-15.958-1.74-7.043-4.172-16.045-5.653-21.452-1.065-3.89-3.589-7.425-7.448-9.363-5.589-2.806-15.945-7.16-28.632-8.517-19.183-2.052-33.671 2.564-43.12 5.893-4.739 1.67-8.977 4.674-12.12 7.337-5.141 4.355-7.535 10.755-7.563 17.129-.042 9.192.632 24.51 4.737 38.966l.409 1.44c6.103 21.51 12.542 44.205 27.779 63.184 4.992 6.218 10.527 12.191 15.713 17.414l-.754 9.458-2.82 35.388-.005.06-.003.061 3.497.157c-3.497-.157-3.497-.154-3.497-.151v.007l-.001.016-.001.04a10.77 10.77 0 00-.003.457c.003.284.017.672.054 1.151.074.958.243 2.284.618 3.865.752 3.166 2.335 7.366 5.64 11.658 6.685 8.683 19.89 17.098 45.517 18.91 25.428 1.799 41.949-6.246 47.891-9.802 2.384-1.427 3.827-3.778 4.306-6.284.768-4.018 2.198-12.284 2.945-21.652.716-8.993.891-20.109.931-26.201.011-1.787.857-3.52 2.312-4.695 8.276-6.681 28.975-24.608 43.246-47.551 9.147-14.707 13.847-28.371 16.261-38.39 1.207-5.009 1.844-9.112 2.18-11.986a67 67 0 00.311-3.351c.025-.392.04-.698.049-.911l.007-.192.024-.253c.022-.25.049-.604.074-1.05.05-.893.091-2.161.06-3.716a56.633 56.633 0 00-1.57-12.137c-2.316-9.51-7.933-21.07-21.082-28.413-6.23-3.479-13.274-5.35-18.639-6.362a83.233 83.233 0 00-6.699-.984 63.872 63.872 0 00-2.592-.217l-.041-.005-.434-.047c-.374-.04-.915-.096-1.591-.158a135.968 135.968 0 00-5.466-.387c-4.383-.222-10.137-.296-15.259.362a63.254 63.254 0 00-6.452 1.17c-7.698 1.816-12.645 8.135-14.785 14.936l-25.455 80.908a1.732 1.732 0 01-.397-1.021 1.868 1.868 0 01.012-.378l.001-.005 12.105-39.016zm-41.678 102.649v.009-.009zm-13.119-8.066l-.005-.007-.001-.001v-.001l-.002-.002-.001-.003-.001-.001-3.741-5.914c2.089 2.065 4.1 3.992 5.971 5.746a316.542 316.542 0 008.702 7.867c.789.686 1.462 1.264 2 1.721a1.14 1.14 0 01-.078.174c-.2.331-.381.478-.458.526l-.008.004c-.404-.032-1.246-.166-5.889-3.48-2.077-1.483-3.725-3.169-4.859-4.499a26.202 26.202 0 01-1.265-1.603 22.14 22.14 0 01-.365-.527z"
          ></path>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M431.94 171.278C431.94 171.278 423.972 164.72 414.317 155.201C411.87 152.789 409.315 150.186 406.753 147.452C403.311 143.779 399.859 139.868 396.642 135.862C381.855 117.443 375.584 95.3492 369.429 73.6615C369.316 73.265 369.204 72.8688 369.091 72.4726C365.117 58.4787 364.446 43.538 364.487 34.4786C364.512 28.939 366.586 23.5853 370.813 20.0041C373.804 17.4692 377.666 14.766 381.833 13.2982C391.122 10.0259 405.017 5.61581 423.418 7.58405C435.547 8.88133 445.485 13.0546 450.852 15.7492C453.701 17.1797 455.653 19.834 456.495 22.9088C457.975 28.3128 460.397 37.2789 462.126 44.2755C464.78 55.0204 466.778 67.7192 466.778 67.7192L454.628 106.878C454.628 106.878 454.311 107.992 454.667 109.378C454.839 110.048 455.168 110.781 455.766 111.483C456.303 112.113 456.94 112.579 457.571 112.923C459.096 113.754 460.591 113.874 460.591 113.874L487.193 29.3189C489.087 23.2998 493.301 18.1873 499.443 16.7382C501.247 16.3125 503.287 15.9237 505.538 15.6341C515.167 14.3954 527.588 15.8865 527.588 15.8865C527.588 15.8865 542.58 16.5796 554.105 23.016C578.2 36.4725 574.812 65.2948 574.812 65.2948C574.812 65.2948 574.36 89.837 556.526 118.51C542.568 140.951 522.234 158.583 514.054 165.187C511.815 166.995 510.459 169.704 510.44 172.582C510.4 178.664 510.226 189.671 509.52 198.528C508.787 207.723 507.382 215.853 506.627 219.801C506.313 221.441 505.394 222.881 503.961 223.739C498.475 227.022 482.662 234.789 458.115 233.053C408.237 229.526 409.54 200.591 409.54 200.591L412.361 165.203C412.361 165.203 414.679 168.87 418.832 172.11C419.138 172.348 419.454 172.585 419.779 172.817C424.525 176.205 425.981 176.822 427.476 176.941C428.971 177.061 430.297 176.163 431.163 174.727C432.03 173.292 431.94 171.278 431.94 171.278ZM406.009 156.787L402.553 200.156L402.547 200.276L409.54 200.591C402.547 200.276 402.547 200.282 402.547 200.288L402.546 200.301L402.545 200.329L402.543 200.393C402.541 200.439 402.54 200.493 402.539 200.554C402.537 200.676 402.536 200.826 402.538 201.004C402.543 201.358 402.559 201.823 402.602 202.382C402.689 203.498 402.883 205.006 403.305 206.785C404.152 210.349 405.922 215.022 409.577 219.77C417.038 229.46 431.305 238.175 457.621 240.036C483.929 241.896 501.158 233.574 507.556 229.745C510.891 227.749 512.858 224.488 513.502 221.116C514.284 217.028 515.738 208.625 516.498 199.084C517.225 189.956 517.4 178.731 517.44 172.628C517.445 171.932 517.78 171.176 518.451 170.634C526.823 163.875 547.887 145.654 562.47 122.207C571.848 107.131 576.676 93.1066 579.162 82.7899C580.405 77.6301 581.065 73.3878 581.415 70.3894C581.59 68.8896 581.688 67.6992 581.743 66.8586C581.77 66.4382 581.787 66.1051 581.796 65.8643C581.798 65.8278 581.799 65.7934 581.801 65.7612C581.806 65.7075 581.811 65.6483 581.817 65.5837C581.842 65.2986 581.872 64.9075 581.899 64.4213C581.954 63.4503 581.997 62.0931 581.963 60.4397C581.897 57.1482 581.526 52.5985 580.295 47.545C577.827 37.4115 571.77 24.8636 557.518 16.9045C550.822 13.1647 543.363 11.203 537.821 10.1587C535.004 9.62787 532.576 9.31478 530.837 9.13324C529.966 9.04227 529.263 8.98378 528.764 8.94741C528.52 8.92964 528.324 8.91712 528.182 8.90865C528.079 8.89704 527.944 8.88216 527.78 8.86462C527.39 8.82282 526.831 8.76583 526.135 8.70208C524.746 8.5748 522.795 8.41945 520.528 8.30474C516.07 8.07925 510.073 7.99303 504.645 8.69128C502.138 9.01376 499.86 9.44755 497.835 9.92528C488.582 12.1085 482.902 19.6338 480.516 27.2181L471.679 55.3052C470.917 51.3683 469.973 46.8516 468.921 42.5965C467.17 35.5082 464.727 26.4695 463.246 21.0602C461.957 16.3527 458.861 11.9378 453.993 9.49346C448.181 6.57516 437.409 2.04051 424.163 0.623752C404.198 -1.51163 389.117 3.31078 379.507 6.69582C374.197 8.5665 369.582 11.872 366.288 14.6635C360.232 19.794 357.52 27.2396 357.487 34.447C357.445 43.7722 358.122 59.4687 362.357 74.3848C362.515 74.94 362.673 75.4966 362.831 76.0545C368.887 97.4034 375.496 120.703 391.184 140.244C395.904 146.125 401.088 151.775 406.009 156.787Z"
            fill="var(--color-background"
          />

          <g filter="url(#filter3_f_124_43)">
            <path
              stroke="url(#paint2_linear_124_43)"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeOpacity="var(--highlight-opacity)"
              strokeWidth="16"
              d="M386.945 25.147c-6.5 5-8.5 8-7 24.5 1.719 18.922 8.5 41 15 53"
            ></path>
          </g>
          <g filter="url(#filter4_f_124_43)">
            <path
              stroke="url(#paint3_linear_124_43)"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeOpacity="var(--highlight-opacity)"
              strokeWidth="16"
              d="M480.945 100.647L500.624 38.9866C502.428 33.3349 506.418 28.5281 512.117 26.8801C512.389 26.8014 512.665 26.7238 512.945 26.6475"
            ></path>
          </g>
        </Letter>

        <defs>
          <filter
            id="filter0_f_124_43"
            width="65.625"
            height="67.001"
            x="0.321"
            y="125.647"
            colorInterpolationFilters="sRGB"
            filterUnits="userSpaceOnUse"
          >
            <feFlood
              floodOpacity="0"
              result="BackgroundImageFix"
            ></feFlood>
            <feBlend
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            ></feBlend>
            <feGaussianBlur
              result="effect1_foregroundBlur_124_43"
              stdDeviation="5"
            ></feGaussianBlur>
          </filter>
          <filter
            id="filter1_f_124_43"
            width="46.002"
            height="120.354"
            x="62.445"
            y="8.146"
            colorInterpolationFilters="sRGB"
            filterUnits="userSpaceOnUse"
          >
            <feFlood
              floodOpacity="0"
              result="BackgroundImageFix"
            ></feFlood>
            <feBlend
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            ></feBlend>
            <feGaussianBlur
              result="effect1_foregroundBlur_124_43"
              stdDeviation="5"
            ></feGaussianBlur>
          </filter>
          <filter
            id="filter2_f_124_43"
            width="104.5"
            height="114.5"
            x="167.945"
            y="11.647"
            colorInterpolationFilters="sRGB"
            filterUnits="userSpaceOnUse"
          >
            <feFlood
              floodOpacity="0"
              result="BackgroundImageFix"
            ></feFlood>
            <feBlend
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            ></feBlend>
            <feGaussianBlur
              result="effect1_foregroundBlur_124_43"
              stdDeviation="5"
            ></feGaussianBlur>
          </filter>
          <filter
            id="filter3_f_124_43"
            width="51.476"
            height="113.502"
            x="361.471"
            y="7.147"
            colorInterpolationFilters="sRGB"
            filterUnits="userSpaceOnUse"
          >
            <feFlood
              floodOpacity="0"
              result="BackgroundImageFix"
            ></feFlood>
            <feBlend
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            ></feBlend>
            <feGaussianBlur
              result="effect1_foregroundBlur_124_43"
              stdDeviation="5"
            ></feGaussianBlur>
          </filter>
          <filter
            id="filter4_f_124_43"
            width="68.004"
            height="110.004"
            x="462.943"
            y="8.645"
            colorInterpolationFilters="sRGB"
            filterUnits="userSpaceOnUse"
          >
            <feFlood
              floodOpacity="0"
              result="BackgroundImageFix"
            ></feFlood>
            <feBlend
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            ></feBlend>
            <feGaussianBlur
              result="effect1_foregroundBlur_124_43"
              stdDeviation="5"
            ></feGaussianBlur>
          </filter>
          <linearGradient
            id="paint0_linear_124_43"
            x1="33.133"
            x2="33.133"
            y1="143.647"
            y2="174.647"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.475" stopColor="var(--highlight-color)" />
            <stop
              offset="1"
              stopColor="var(--highlight-color)"
              stopOpacity="0.7"
            />
          </linearGradient>
          <linearGradient
            id="paint1_linear_124_43"
            x1="200.5"
            x2="194.5"
            y1="30"
            y2="112.5"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.568" stopColor="var(--highlight-color)" />
            <stop
              offset="1"
              stopColor="var(--highlight-color)"
              stopOpacity="0.6"
            />
          </linearGradient>
          <linearGradient
            id="paint2_linear_124_43"
            x1="387.209"
            x2="387.209"
            y1="25.147"
            y2="102.647"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.47" stopColor="var(--highlight-color)" />
            <stop
              offset="1"
              stopColor="var(--highlight-color)"
              stopOpacity="0.3"
            />
          </linearGradient>
          <linearGradient
            id="paint3_linear_124_43"
            x1="496.945"
            x2="496.945"
            y1="26.647"
            y2="100.647"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.535" stopColor="var(--highlight-color)" />
            <stop
              offset="1"
              stopColor="var(--highlight-color)"
              stopOpacity="0.26"
            />
          </linearGradient>
        </defs>
      </Svg>
      <Subtitle
        src="/images/joy-for-js/for-js-devs-curved.png"
        alt=""
      />
    </Wrapper>
  );
}

const Wrapper = styled.a`
  position: relative;
  --color-primary: oklch(0.87 0.21 91.16);
  --color-background: ${DARK_COLORS['--color-background']};
  --highlight-color: white;
`;

const Svg = styled.svg`
  display: block;
  /* Don’t clip the letters when they bounce into view */
  overflow: visible;
`;

const Letter = styled(animated.g)`
  transform-origin: center center;
  animation: fadeIn 200ms both;
`;

const Subtitle = styled.img`
  width: 85%;
  position: absolute;
  right: 16px;
  bottom: -8px;
  aspect-ratio: 1089 / 198;
  animation: fadeIn 500ms both;
  animation-delay: 750ms;

  @keyframes clipIn {
    from {
      clip-path: polygon(0 0, 0 0, 0 100%, 0 100%);
    }
    to {
      clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    }
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: clipIn 1500ms both cubic-bezier(0.31, 0, 0.17, 1.01);
    animation-delay: 750ms;
  }
`;

export default JoyLogo;
